import React from "react";

const cardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "20px",
  marginBottom: "20px",
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#111827",
};

const descStyle = {
  fontSize: "12px",
  color: "#6b7280",
  marginTop: "4px",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "14px",
  padding: "10px 0",
  borderBottom: "1px solid #f3f4f6",
};

const requirementLabels = {
  primaryCategories: "Primary Categories",
  policyTerms: "Store Terms",
  policyRefund: "Refund Policy",
  policyCancellation: "Cancellation Policy",
};

const policyOptions = [
  { value: "warn_only", label: "Warn only" },
  { value: "block_new", label: "Block publishing" },
  { value: "full", label: "Block and hide storefront" },
];

const enforcementModeOptions = [
  { value: "monitor", label: "Monitor only" },
  { value: "warn", label: "Warn only" },
  { value: "block_new", label: "Block new publishing" },
  { value: "full", label: "Full enforcement" },
];

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      width: "52px",
      height: "30px",
      border: "none",
      borderRadius: "999px",
      cursor: "pointer",
      background: checked ? "#2563eb" : "#d1d5db",
      position: "relative",
      transition: "background 0.2s ease",
      flexShrink: 0,
    }}
    aria-label="Toggle setting"
  >
    <span
      style={{
        position: "absolute",
        top: "3px",
        left: checked ? "25px" : "3px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        background: "white",
        transition: "left 0.2s ease",
      }}
    />
  </button>
);

const StorefrontGatingSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const defaultStorefrontGating = {
    enabled: true,
    rolloutStartedAt: new Date().toISOString(),
    enforcementMode: "full",
    defaultGraceDays: 30,
    existingSellerDefaultPolicy: "full",
    newSellerDefaultPolicy: "block_new",
    requirements: {
      primaryCategories: {
        enabled: true,
        policy: "block_new",
        newSellerPolicy: "block_new",
        existingSellerPolicy: "full",
        graceDays: 30,
      },
      policyTerms: {
        enabled: true,
        policy: "block_new",
        newSellerPolicy: "block_new",
        existingSellerPolicy: "full",
        graceDays: 30,
      },
      policyRefund: {
        enabled: true,
        policy: "block_new",
        newSellerPolicy: "block_new",
        existingSellerPolicy: "full",
        graceDays: 30,
      },
      policyCancellation: {
        enabled: true,
        policy: "block_new",
        newSellerPolicy: "block_new",
        existingSellerPolicy: "full",
        graceDays: 30,
      },
    },
  };

  const storefrontGating = {
    ...defaultStorefrontGating,
    ...(settings?.storefrontGating || {}),
    requirements: {
      ...defaultStorefrontGating.requirements,
      ...(settings?.storefrontGating?.requirements || {}),
    },
  };

  const storefrontSettings = {
    storefrontHandleChangeCooldownDays:
      settings?.storefrontSettings?.storefrontHandleChangeCooldownDays ?? null,
  };

  const updateStorefrontGatingField = (field, value) => {
    updateSettings({
      storefrontGating: {
        ...storefrontGating,
        [field]: value,
      },
    });
  };

  const updateStorefrontRequirement = (requirementKey, patch) => {
    updateSettings({
      storefrontGating: {
        ...storefrontGating,
        requirements: {
          ...storefrontGating.requirements,
          [requirementKey]: {
            ...(storefrontGating.requirements?.[requirementKey] || {}),
            ...patch,
          },
        },
      },
    });
  };

  const applyPreset = (preset) => {
    const baseRequirements = {
      primaryCategories: { enabled: true },
      policyTerms: { enabled: true },
      policyRefund: { enabled: true },
      policyCancellation: { enabled: true },
    };

    if (preset === "soft") {
      updateSettings({
        storefrontGating: {
          ...storefrontGating,
          enabled: true,
          enforcementMode: "warn",
          defaultGraceDays: 30,
          newSellerDefaultPolicy: "warn_only",
          existingSellerDefaultPolicy: "warn_only",
          requirements: {
            ...storefrontGating.requirements,
            primaryCategories: {
              ...baseRequirements.primaryCategories,
              ...(storefrontGating.requirements?.primaryCategories || {}),
              newSellerPolicy: "warn_only",
              existingSellerPolicy: "warn_only",
              graceDays: 30,
            },
            policyTerms: {
              ...baseRequirements.policyTerms,
              ...(storefrontGating.requirements?.policyTerms || {}),
              newSellerPolicy: "warn_only",
              existingSellerPolicy: "warn_only",
              graceDays: 30,
            },
            policyRefund: {
              ...baseRequirements.policyRefund,
              ...(storefrontGating.requirements?.policyRefund || {}),
              newSellerPolicy: "warn_only",
              existingSellerPolicy: "warn_only",
              graceDays: 30,
            },
            policyCancellation: {
              ...baseRequirements.policyCancellation,
              ...(storefrontGating.requirements?.policyCancellation || {}),
              newSellerPolicy: "warn_only",
              existingSellerPolicy: "warn_only",
              graceDays: 30,
            },
          },
        },
      });
      return;
    }

    if (preset === "legacy-safe") {
      updateSettings({
        storefrontGating: {
          ...storefrontGating,
          enabled: true,
          enforcementMode: "full",
          defaultGraceDays: 45,
          newSellerDefaultPolicy: "block_new",
          existingSellerDefaultPolicy: "full",
          requirements: {
            ...storefrontGating.requirements,
            primaryCategories: {
              ...baseRequirements.primaryCategories,
              ...(storefrontGating.requirements?.primaryCategories || {}),
              newSellerPolicy: "block_new",
              existingSellerPolicy: "full",
              graceDays: 45,
            },
            policyTerms: {
              ...baseRequirements.policyTerms,
              ...(storefrontGating.requirements?.policyTerms || {}),
              newSellerPolicy: "block_new",
              existingSellerPolicy: "full",
              graceDays: 45,
            },
            policyRefund: {
              ...baseRequirements.policyRefund,
              ...(storefrontGating.requirements?.policyRefund || {}),
              newSellerPolicy: "block_new",
              existingSellerPolicy: "full",
              graceDays: 45,
            },
            policyCancellation: {
              ...baseRequirements.policyCancellation,
              ...(storefrontGating.requirements?.policyCancellation || {}),
              newSellerPolicy: "block_new",
              existingSellerPolicy: "full",
              graceDays: 45,
            },
          },
        },
      });
      return;
    }

    // strict
    updateSettings({
      storefrontGating: {
        ...storefrontGating,
        enabled: true,
        enforcementMode: "full",
        defaultGraceDays: 30,
        newSellerDefaultPolicy: "block_new",
        existingSellerDefaultPolicy: "full",
        requirements: {
          ...storefrontGating.requirements,
          primaryCategories: {
            ...baseRequirements.primaryCategories,
            ...(storefrontGating.requirements?.primaryCategories || {}),
            newSellerPolicy: "block_new",
            existingSellerPolicy: "full",
            graceDays: 30,
          },
          policyTerms: {
            ...baseRequirements.policyTerms,
            ...(storefrontGating.requirements?.policyTerms || {}),
            newSellerPolicy: "block_new",
            existingSellerPolicy: "full",
            graceDays: 30,
          },
          policyRefund: {
            ...baseRequirements.policyRefund,
            ...(storefrontGating.requirements?.policyRefund || {}),
            newSellerPolicy: "block_new",
            existingSellerPolicy: "full",
            graceDays: 30,
          },
          policyCancellation: {
            ...baseRequirements.policyCancellation,
            ...(storefrontGating.requirements?.policyCancellation || {}),
            newSellerPolicy: "block_new",
            existingSellerPolicy: "full",
            graceDays: 30,
          },
        },
      },
    });
  };

  return (
    <div>
      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>
          🛡️ Storefront Gating Policy
        </h3>
        <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
          Always-on seller compliance controls with gradual enforcement and configurable grace windows.
        </p>

        <div style={{ ...cardStyle, marginTop: "20px" }}>
          <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>
            🏷️ Storefront Handle Policy
          </h3>
        <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: "14px" }}>
          Control how often sellers can change their storefront handle. Leave blank to disable the cooldown.
        </p>

        <div style={{ ...rowStyle, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Handle Change Cooldown (days)</div>
            <div style={descStyle}>
              Applies to R1, R4, and R7 storefront handle changes in storefront setup. Blank or 0 disables cooldown.
            </div>
          </div>
          <input
            type="number"
            min="0"
            max="365"
            value={
              storefrontSettings.storefrontHandleChangeCooldownDays ?? ""
            }
            onChange={(e) => {
              const rawValue = e.target.value;
              updateSettings({
                storefrontSettings: {
                  ...storefrontSettings,
                  storefrontHandleChangeCooldownDays:
                    rawValue === "" ? null : Math.max(0, Number(rawValue)),
                },
              });
            }}
            style={{
              width: "110px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "8px 10px",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      <div
        style={{
          marginTop: "14px",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
          <button
            type="button"
            onClick={() => applyPreset("soft")}
            style={{
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              color: "#1d4ed8",
              borderRadius: "8px",
              padding: "8px 12px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Apply Soft Launch Preset
          </button>
          <button
            type="button"
            onClick={() => applyPreset("strict")}
            style={{
              border: "1px solid #86efac",
              background: "#f0fdf4",
              color: "#166534",
              borderRadius: "8px",
              padding: "8px 12px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Apply Strict Launch Preset
          </button>
          <button
            type="button"
            onClick={() => applyPreset("legacy-safe")}
            style={{
              border: "1px solid #fed7aa",
              background: "#fff7ed",
              color: "#9a3412",
              borderRadius: "8px",
              padding: "8px 12px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Apply Legacy-Safe Preset
          </button>
        </div>

        <div style={{ marginTop: "16px" }}>
          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Enable Storefront Gating</div>
              <div style={descStyle}>Global switch for storefront setup enforcement.</div>
            </div>
            <Toggle
              checked={storefrontGating.enabled !== false}
              onChange={(value) => updateStorefrontGatingField("enabled", value)}
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Enforcement Mode</div>
              <div style={descStyle}>Controls whether the system only informs, blocks publishing, or also hides non-compliant storefronts.</div>
            </div>
            <select
              value={storefrontGating.enforcementMode || "full"}
              onChange={(e) =>
                updateStorefrontGatingField("enforcementMode", e.target.value)
              }
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                background: "white",
              }}
            >
              {enforcementModeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Default Grace (days)</div>
              <div style={descStyle}>Applied to requirements that do not override graceDays.</div>
            </div>
            <input
              type="number"
              min="0"
              max="365"
              value={Number(storefrontGating.defaultGraceDays ?? 30)}
              onChange={(e) =>
                updateStorefrontGatingField(
                  "defaultGraceDays",
                  Math.max(0, Math.min(365, Number(e.target.value) || 0)),
                )
              }
              style={{
                width: "120px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>Existing Seller Default Action</div>
              <div style={descStyle}>Fallback policy when a requirement policy is not explicitly set.</div>
            </div>
            <select
              value={storefrontGating.existingSellerDefaultPolicy || "full"}
              onChange={(e) =>
                updateStorefrontGatingField(
                  "existingSellerDefaultPolicy",
                  e.target.value,
                )
              }
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                background: "white",
              }}
            >
              {policyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ ...rowStyle, borderBottom: "none" }}>
            <div>
              <div style={labelStyle}>New Seller Default Action</div>
              <div style={descStyle}>Fallback policy for sellers with no active products.</div>
            </div>
            <select
              value={storefrontGating.newSellerDefaultPolicy || "block_new"}
              onChange={(e) =>
                updateStorefrontGatingField("newSellerDefaultPolicy", e.target.value)
              }
              style={{
                width: "180px",
                padding: "8px 10px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                background: "white",
              }}
            >
              {policyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: "14px", display: "grid", gap: "12px" }}>
          {["primaryCategories", "policyTerms", "policyRefund", "policyCancellation"].map(
            (requirementKey) => {
              const requirement = storefrontGating.requirements?.[requirementKey] || {};
              return (
                <div
                  key={requirementKey}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    padding: "12px",
                    background: "#fafafa",
                  }}
                >
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                    {requirementLabels[requirementKey] || requirementKey}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    Configure new seller action vs existing seller action after grace period.
                  </div>
                  <div
                    style={{
                      marginTop: "10px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                      gap: "10px",
                    }}
                  >
                    <label style={{ fontSize: "12px", color: "#374151" }}>
                      Enabled
                      <select
                        value={requirement.enabled === false ? "false" : "true"}
                        onChange={(e) =>
                          updateStorefrontRequirement(requirementKey, {
                            enabled: e.target.value === "true",
                          })
                        }
                        style={{
                          width: "100%",
                          marginTop: "6px",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          background: "white",
                        }}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    </label>

                    <label style={{ fontSize: "12px", color: "#374151" }}>
                      New Seller Action
                      <select
                        value={requirement.newSellerPolicy || requirement.policy || "block_new"}
                        onChange={(e) =>
                          updateStorefrontRequirement(requirementKey, {
                            newSellerPolicy: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          marginTop: "6px",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          background: "white",
                        }}
                      >
                        {policyOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label style={{ fontSize: "12px", color: "#374151" }}>
                      Existing Seller Action
                      <select
                        value={requirement.existingSellerPolicy || requirement.policy || "full"}
                        onChange={(e) =>
                          updateStorefrontRequirement(requirementKey, {
                            existingSellerPolicy: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          marginTop: "6px",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          background: "white",
                        }}
                      >
                        {policyOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label style={{ fontSize: "12px", color: "#374151" }}>
                      Grace Days
                      <input
                        type="number"
                        min="0"
                        max="365"
                        value={Number(requirement.graceDays ?? storefrontGating.defaultGraceDays ?? 30)}
                        onChange={(e) =>
                          updateStorefrontRequirement(requirementKey, {
                            graceDays: Math.max(
                              0,
                              Math.min(365, Number(e.target.value) || 0),
                            ),
                          })
                        }
                        style={{
                          width: "100%",
                          marginTop: "6px",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                        }}
                      />
                    </label>
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
        <button
          onClick={() => saveSettings()}
          disabled={saving || !hasUnsavedChanges}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            background: saving || !hasUnsavedChanges ? "#9ca3af" : "#2563eb",
            color: "white",
            fontWeight: 600,
            cursor: saving || !hasUnsavedChanges ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : "Save Storefront Gating Settings"}
        </button>

        <button
          onClick={resetSettings}
          disabled={saving || !hasUnsavedChanges}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            background: "white",
            color: "#374151",
            fontWeight: 600,
            cursor: saving || !hasUnsavedChanges ? "not-allowed" : "pointer",
            opacity: saving || !hasUnsavedChanges ? 0.5 : 1,
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default StorefrontGatingSettings;
